/**
 * Store: Accessibility
 * -----------------------------------------------------------------------------
 * Handles focus styles, trap focus, and previous element states.
 * - Tied to the accessibility component.
 * - Only JSDOC custom parameters, ignore module state or object.
 *
 */
import { defineStore } from 'pinia';
import { focusable } from '~/helpers/accessibility';

const useAccessibilityStore = defineStore('accessibility', {
  state: () => ({
    focusStyles: false,
    focusTrap: [],
    previousElement: false,
  }),

  getters: {
    /**
     * Get if element is inside focus trap.
     * @param {HTMLElement} element - Element to test.
     * @returns {Boolean}
     */
    getElementInFocusTrap: (state) => (element) => {
      return state.focusTrap.includes(element);
    },

    /**
     * Get first focus trap element.
     * @returns {HTMLElement|Null}
     */
    getFirstElement(state) {
      return state.focusTrap[0];
    },

    /**
     * Get if focus should be trapped (focusTrap has elements).
     * @returns {Boolean}
     */
    getFocusTrapActive(state) {
      return Boolean(state.focusTrap.length);
    },

    /**
     * Get last focus trap element.
     * @returns {HTMLElement|Null}
     */
    getLastElement(state) {
      return state.focusTrap[state.focusTrap.length - 1];
    },
  },

  actions: {
    /**
     * Clears focus trap elements.
     */
    clearFocusTrap() {
      this.focusTrap = [];
    },

    /**
     * Commit focus styles state.
     */
    setFocusStyles() {
      this.focusStyles = true;
    },

    /**
     * Commit focus trap elements.
     * - Must be provided either container or elements.
     * @param {Object} config - Config object.
     * @param {HTMLElement|Array} [config.container] - Single container element
     * or array of containers to find focusable elements inside of.
     * @param {Array} [config.elements] - Array of focusable elements.
     * @param {Boolean} [config.includeDisplayNone] - Include `display: none`
     * elements.
     * @param {Boolean} [config.includeHidden] - Include hidden elements.
     * @param {Boolean} [config.includeNonTabbable] - Include elements with -1
     * tabindex.
     * @param {Boolean} [config.prepend] - Prepend elements instead of append.
     */
    setFocusTrap({
      container,
      elements = [],
      includeDisplayNone = false,
      includeHidden = true,
      includeNonTabbable = false,
      prepend = false,
    } = {}) {
      if (!container && !elements.length) {
        throw new Error('No container or elements to trap focus on');
      }

      const focusableElements = [];

      if (prepend && elements.length) {
        focusableElements.push(...elements);
      }

      const containers = Array.isArray(container) ? container : [container];

      containers.forEach((element) => {
        focusableElements.push(
          ...focusable(element, {
            includeDisplayNone,
            includeHidden,
            includeNonTabbable,
          }),
        );
      });

      if (!prepend && elements.length) {
        focusableElements.push(...elements);
      }

      /**
       * Sort trap focus by tabindex if value isn't 0.
       */
      focusableElements.sort((first, second) => {
        const firstIndex = first.getAttribute('tabindex');
        const secondIndex = second.getAttribute('tabindex');

        if (firstIndex < secondIndex) {
          return -1;
        }

        if (firstIndex > secondIndex) {
          return 1;
        }

        return 0;
      });

      this.focusTrap = focusableElements;
    },

    /**
     * Commit previous element state.
     * @param {HTMLElement} [previousElement] - Previous element.
     * false to unset.
     */
    setPreviousElement(previousElement = false) {
      this.previousElement = previousElement;
    },
  },
});

export default useAccessibilityStore;
